import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { parse } from "query-string";
import { useLocation, useHistory } from "react-router-dom";
import {
  MeuApeApp,
  RuntimeEnviroment,
  MeuApeWrapper as BaseMeuApeWrapper,
  ScheduledMaintenance,
  meuApeUrls
} from "@company-mrv/mrv-design-system/components";
import AuthContext from "contexts/auth";
import ContractContext from "contexts/contract";
import GeneralContext from "contexts/general";
import { ROUTES, REACT_APP_ENV } from "utils/constants";
import { isRoutePublic } from "utils/functions";
import { getThemeName } from "utils/pluginHelpers";
import {
  trackException,
  trackTrace,
  trackTraceException
} from "services/trackInsights";
import VAContext from "contexts/va";
import { ContainerBaseMeuApeWrapper } from "./styles";

const MeuApeWrapper = ({ children }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { user, logout } = useContext(AuthContext);
  const { contracts, selectedContract, setSelectedContract } = useContext(
    ContractContext
  );
  const { status, notShowProgress } = useContext(VAContext);
  const {
    setGeneralLoading,
    redirect,
    financialLink,
    setRedirectFinancialLink
  } = useContext(GeneralContext);
  const [authUser, setAuthUser] = useState(user);

  useEffect(() => {
    if (isRoutePublic(pathname)) {
      setAuthUser(null);
    } else {
      setAuthUser(user);
    }
  }, [pathname, user]);

  const isQueryParameterDefined = value => {
    return (
      value != null && value !== "" && value !== "undefined" && value !== "null"
    );
  };

  const getSelectedContract = contractsList => {
    const params = parse(history.location.search);
    let selectContract;
    if (isQueryParameterDefined(params.contractId)) {
      trackTrace(
        `Contract Id obtido a partir da url, valor: ${params.contractId}`
      );
      selectContract = contractsList.find(
        contract => contract.unidadeId === params.contractId
      );
    }
    return selectContract || contractsList[0];
  };

  const getName = contract => {
    return contract.nomeCompletoUnidade;
  };

  useEffect(() => {
    if (contracts.length === 0) return;

    const contractToSelect =
      selectedContract && selectedContract.nomeEmpreendimento
        ? selectedContract
        : getSelectedContract(contracts);

    if (selectedContract.unidadeId !== contractToSelect.unidadeId) {
      setSelectedContract(contractToSelect);
    }
    setRedirectFinancialLink(contractToSelect);
  }, [contracts, selectedContract, pathname]);

  const getUnitList = () => {
    return contracts.map(contract => ({
      ...contract,
      id: contract.unidadeId,
      name: getName(contract) || "",
      isBeta: true,
      empreendimentoId: contract.empreendimentoId
    }));
  };

  const changeCurrentUnit = async contractSelected => {
    if (contractSelected.isGarageContract) {
      redirect(financialLink, true);
    } else {
      try {
        setSelectedContract(contractSelected);
        setRedirectFinancialLink(contractSelected);
      } catch (error) {
        trackException(error);
        trackTraceException("changeCurrentUnit", error);
        history.push({
          pathname: ROUTES.ERROR,
          state: { detail: "changeCurrentUnit" }
        });
      }
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem("SCOPE_LOCAL_STORAGE_KEY_CHECKLIST");
    setGeneralLoading(true);
    await logout();
  };

  const hideHeaderAndFooter =
    pathname === ROUTES.LOGIN ||
    pathname === ROUTES.LOGOUT ||
    pathname === ROUTES.VISTORIA_ANTECIPADA_ONBOARDING ||
    pathname === ROUTES.VISTORIA_ANTECIPADA_LOCATION ||
    pathname === ROUTES.ENTREGA_DE_CHAVES_LOCATION;

  const currentUnit = selectedContract
    ? {
        ...selectedContract,
        id: selectedContract.unidadeId,
        name: getName(selectedContract) || "",
        isBeta: true,
        empreendimentoId: selectedContract.empreendimentoId
      }
    : "";

  const hideFooter =
    pathname.indexOf(ROUTES.VISTORIA_ANTECIPADA_CHECKLIST) > -1 ||
    pathname.indexOf(ROUTES.ENTREGA_DE_CHAVES_TERM) > -1;

  const hideMenu = pathname.indexOf(ROUTES.VISTORIA_ANTECIPADA_ENTRADA_ID) > -1;

  const isMaintenanceBypassRoute = () => {
    return (
      pathname.includes("/vistoria-antecipada") ||
      pathname.includes("/entrar-mock") ||
      pathname.includes("/entrar") ||
      pathname.includes("/sair") ||
      pathname.includes("/inspection/login") ||
      pathname.includes("/inspection/login-cohab")
    );
  };

  const checkRuntimeEnviroment = () => {
    switch (REACT_APP_ENV) {
      case "DEV":
        return RuntimeEnviroment.Development;
      case "QAS":
        return RuntimeEnviroment.Qas;
      case "PRD":
        return RuntimeEnviroment.Production;
      case "SIM":
        return RuntimeEnviroment.Sim;
      default:
        return null;
    }
  };

  const onSoftLogout = user?.isSoftauth
    ? () => history.replace(ROUTES.LOGOUT)
    : null;

  const authToken = !user || user.isSoftauth ? null : authUser;

  meuApeUrls.setRuntimeEnvironment(checkRuntimeEnviroment());

  return (
    <ScheduledMaintenance
      themeName={getThemeName()}
      currentApplication={MeuApeApp.Relacionamento}
      authToken={authToken?.access_token}
      bypassMaintenance={isMaintenanceBypassRoute()}
    >
      <ContainerBaseMeuApeWrapper iconShort={!!status}>
        <BaseMeuApeWrapper
          authToken={authToken}
          currentUnit={currentUnit}
          currentApplication={MeuApeApp.Relacionamento}
          unitList={getUnitList()}
          onCurrentUnitChange={changeCurrentUnit}
          onLogout={handleLogout}
          hideHeader={hideHeaderAndFooter}
          hideFooter={hideHeaderAndFooter || hideFooter}
          runtimeEnvironment={checkRuntimeEnviroment()}
          theme={{
            name: getThemeName()
          }}
          iconShort={!!status}
          activateProgress={
            !!status &&
            status.steps &&
            (status.currentStep || status.currentStep === 0) &&
            !notShowProgress
              ? {
                  steps: status.steps,
                  currentStep: status.currentStep
                }
              : null
          }
          hideMenuButton={hideFooter || hideMenu}
          onSoftLogout={onSoftLogout}
          pageHeaderTitle={status ? "Vistoria" : null}
          removeRightItems={notShowProgress}
        >
          {children}
        </BaseMeuApeWrapper>
      </ContainerBaseMeuApeWrapper>
    </ScheduledMaintenance>
  );
};

MeuApeWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

export default MeuApeWrapper;
